import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { FormHelperText } from '@material-ui/core';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import FormControl from '@material-ui/core/FormControl';
import Input from '@material-ui/core/Input';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import { createEmployeeDirectoryImport } from '../employee-directory-import/actions/create-employee-directory-import';
import { loadEmployeeDirectoryImports } from '../employee-directory-import/actions/load-employee-directory-imports';
import { employeeDirectoryImportForOrgIdSelector } from '../employee-directory-import/employee-directory-import-selectors';
import { userSelector } from '../reducers/current-user-selectors';

import { uploadFileViaEmployer } from '../api/file-upload-api-client';
import { UiButton } from '../ui/ui-button';
import { UiDialog } from '../ui/ui-dialog';
import { employeesRoute } from '../web-routes';

import {
  isSupportedFileExtension,
  PERMITTED_FILE_TYPES,
  getFileKey,
} from './employee-import-utils';
import EmployeeListExample from './employee_list_example.csv';

import { FILES_BUCKET } from '../config/aws-config';
import { combineWords, formatDateTimeShort } from '../util/formatters';
import { useThunkDispatch } from '../util/use-thunk-dispatch';
import { ErrorMessage } from '../widgets/error-message';
import Message from '../widgets/legacy-message';
import { TableHeaderCell } from '../widgets/table-components/table-header-cell';

export const EmployeeListUploadDialog = ({
  org,
  closeDialog,
  hideRequestSentDialog,
  hasEmployeeList,
}: {
  org: Org;
  closeDialog: () => void;
  hideRequestSentDialog?: boolean;
  hasEmployeeList?: boolean;
}) => {
  const orgId = org.org_id;
  const user = useSelector((state: any) => userSelector(state));
  const employeeDirectoryImports = useSelector((state: any) =>
    employeeDirectoryImportForOrgIdSelector(state, orgId),
  );
  const dispatch = useDispatch();
  const thunkDispatch = useThunkDispatch();
  const history = useHistory();

  useEffect(() => {
    dispatch(loadEmployeeDirectoryImports(orgId));
  });

  const [employeeListFile, setEmployeeListFile] = useState<File | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [isRequestSentDialogOpen, setIsRequestSentDialogOpen] = useState<boolean>(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);

  const closeDialogs = () => {
    setIsRequestSentDialogOpen(false);
    setIsErrorDialogOpen(false);
    closeDialog();
  };

  const setDialogState = (setDialogOpen: (isOpen: boolean) => void, isOpen: boolean) => () => {
    if (isOpen) {
      closeDialogs();
    }
    setDialogOpen(isOpen);
  };

  const onEmployeeListChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files || files.length === 0) {
      return;
    }

    if (!isSupportedFileExtension(files[0])) {
      setUploadError(
        `Invalid file. Please upload a file ending in ${combineWords(PERMITTED_FILE_TYPES, 'or')}`,
      );
      return;
    }

    setEmployeeListFile(files[0]);
    setUploadError(null);
  };

  const handleOpenHrisDialogClick = async () => {
    history.push(employeesRoute(orgId, { openHrisDialog: true }));
  };

  const createImport = async (employeeDirectoryImport: EmployeeDirectoryImportCreateRequest) =>
    thunkDispatch(createEmployeeDirectoryImport(employeeDirectoryImport));

  const onEmployeeListUpload = async () => {
    if (!employeeListFile || !org) {
      return;
    }

    setIsUploadingFile(true);
    const fileKey = getFileKey(employeeListFile, org);

    try {
      await uploadFileViaEmployer(fileKey, employeeListFile);
    } catch (e) {
      setIsUploadingFile(false);
      closeDialog();
      return;
    }

    await createImport({
      orgId,
      originalFileName: employeeListFile.name,
      employeeListPath: `${FILES_BUCKET}/${fileKey}`,
    });

    setIsUploadingFile(false);
    setIsRequestSentDialogOpen(!hideRequestSentDialog);
    closeDialog();
  };

  const hasUploadError = Boolean(uploadError);
  const hasUploadedEmployeeList =
    hasEmployeeList || !!org.employeeListPath || !!employeeDirectoryImports.length;

  return (
    <>
      (
      <UiDialog
        onClose={closeDialog}
        title={hasUploadedEmployeeList ? 'Upload Another List' : 'Upload A List'}
      >
        <UiDialog.Content>
          {hasUploadedEmployeeList ? (
            <Message variant="informational" canBeDismissed={false} className="u-margin-bottom-md">
              <div>
                By uploading a new list, new employees will be added, existing employees will be
                updated, and anyone currently in your directory who is not on the new list{' '}
                <span className="typo-bold">will be marked as terminated</span>. All message
                history, groups, etc will be saved.
              </div>
            </Message>
          ) : null}
          <div>Please upload an Excel or CSV file including:</div>
          <div className="u-display-flex u-margin-top-md">
            <div className="u-flexGrow-1 u-flex-1">
              <div className="typo-bold">Required:</div>
              <ul>
                <li>First name</li>
                <li>Last name</li>
                <li>Cell phone number</li>
                <li>Start Date</li>
                <li>Employee Identifier</li>
              </ul>
            </div>
            <div className="u-flexGrow-1 u-flex-1">
              <div className="typo-bold">And optionally:</div>
              <ul>
                <li>Email</li>
                <li>Birthdate</li>
                <li>Supervisor</li>
                <li>Helpful groupings as columns (department, job title, location, etc.)</li>
                <li>Language preference (English, Spanish, etc.)</li>
              </ul>
            </div>
          </div>
          <div>
            <a className="typo-link u-color-secondary" href={EmployeeListExample}>
              View Example Employee List.
            </a>{' '}
            This will often be an export from an existing system such as scheduling or payroll.
          </div>
          {user.isAccountAdmin ? (
            <div className="u-margin-y-lg">
              Alternatively, Team Engine can keep your directory up to date with many common HRIS
              systems such as ADP, BambooHR, Paylocity, Paychex, UKG, etc.{' '}
              <a
                className="typo-link u-color-secondary"
                href="https://help.teamengine.io/article/85-hris-integration"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more
              </a>
            </div>
          ) : null}
          <FormControl margin="dense">
            <Input
              type="file"
              margin="dense"
              onChange={onEmployeeListChange}
              error={hasUploadError}
              inputProps={{ accept: PERMITTED_FILE_TYPES }}
              disabled={isUploadingFile}
            />
            <FormHelperText error>{uploadError}</FormHelperText>
          </FormControl>
          {employeeDirectoryImports.length ? (
            <Accordion>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
              >
                Past Lists
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableHeaderCell>Filename</TableHeaderCell>
                      <TableHeaderCell>Import Date</TableHeaderCell>
                      <TableHeaderCell>Completion Date</TableHeaderCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {employeeDirectoryImports.map((ed) => (
                      <TableRow key={ed.employeeDirectoryImportId}>
                        <TableCell>{ed.originalFileName}</TableCell>
                        <TableCell>{formatDateTimeShort(ed.importedAt)}</TableCell>
                        <TableCell>
                          {ed.completedAt ? formatDateTimeShort(ed.completedAt) : null}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ) : null}
        </UiDialog.Content>
        <UiDialog.Actions>
          <UiButton onClick={closeDialog} variant="dismiss" disabled={isUploadingFile}>
            Cancel
          </UiButton>
          <div>
            {user.isAccountAdmin ? (
              <UiButton
                className="u-margin-right-lg"
                variant="secondary"
                disabled={isUploadingFile}
                onClick={handleOpenHrisDialogClick}
              >
                Sync With HR/Payroll
              </UiButton>
            ) : null}
            <UiButton
              disabled={hasUploadError}
              onClick={onEmployeeListUpload}
              loading={isUploadingFile}
            >
              Upload
            </UiButton>
          </div>
        </UiDialog.Actions>
      </UiDialog>
      )
      {isRequestSentDialogOpen ? (
        <UiDialog
          onClose={setDialogState(setIsRequestSentDialogOpen, false)}
          title="Employee List Uploaded"
        >
          <UiDialog.Content>
            <div className="typo-title">
              We are adding your employees to the system. You will receive an email within a few
              hours when this is complete.
            </div>
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton onClick={setDialogState(setIsRequestSentDialogOpen, false)}>Ok</UiButton>
          </UiDialog.Actions>
        </UiDialog>
      ) : null}
      {isErrorDialogOpen ? (
        <UiDialog onClose={setDialogState(setIsErrorDialogOpen, false)}>
          <UiDialog.Content>
            <ErrorMessage />
          </UiDialog.Content>
          <UiDialog.Actions>
            <UiButton onClick={setDialogState(setIsErrorDialogOpen, false)}>Ok</UiButton>
          </UiDialog.Actions>
        </UiDialog>
      ) : null}
    </>
  );
};
